import { makeStyles } from "tss-react/mui"
import { CSSObject } from "tss-react"
import { iconButtonClasses, iconClasses } from "@mui/material"
import { blueGrey, lightBlue } from "../../core/palette"

const colorStyles = (
  color: Record<string | number, string>,
  variant: "default" | "outlined" | "solid"
): CSSObject => ({
  color: color[500],

  ...(variant === "outlined" && {
    border: `1px solid ${color[100]}`
  }),

  ...(variant === "solid" && {
    backgroundColor: color[50],
    color: color[500]
  }),

  "&:hover, &:focus, &:focus-visible": {
    backgroundColor: color[200],
    color: color[800]
  },

  [`&.${iconButtonClasses.disabled}`]: {
    color: color[200]
  }
})

type StylesParams = {
  variant?: "default" | "outlined" | "solid"
  squareHover?: boolean
}

export const useStyles = makeStyles<StylesParams>()(
  (
    { typography, spacing, mixins },
    { variant = "default", squareHover = false }
  ) => ({
    root: {
      boxSizing: "border-box",
      padding: 0,
      ...colorStyles(blueGrey, variant),

      ...(squareHover && {
        borderRadius: "3px"
      }),

      "&:focus, &:focus-visible": {
        ...mixins.focusRing.shadow,
        boxShadow: `${mixins.focusRing.shadow.boxShadow} !important`,
        border: "none",
        backgroundColor: "transparent"
      },

      // center icon in button container
      [`& .${iconClasses.root}`]: {
        width: "inherit",
        height: "inherit",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }
    },

    // 30, 36, 50, 60px - size (width/height)
    sizeXsmall: {
      width: "1.875rem",
      height: "1.875rem",

      [`& .${iconClasses.root}`]: {
        fontSize: typography.pxToRem(14),
        lineHeight: typography.pxToRem(16),
        padding: spacing(2)
      }
    },
    sizeSmall: {
      width: "2.25rem",
      height: "2.25rem",

      [`& .${iconClasses.root}`]: {
        fontSize: typography.pxToRem(14),
        lineHeight: typography.pxToRem(16),
        padding: "11px"
      }
    },
    sizeMedium: {
      width: "3.125rem",
      height: "3.125rem",

      [`& .${iconClasses.root}`]: {
        fontSize: typography.pxToRem(18),
        lineHeight: typography.pxToRem(22),
        padding: spacing(4)
      }
    },
    sizeLarge: {
      width: "3.75rem",
      height: "3.75rem",

      [`& .${iconClasses.root}`]: {
        fontSize: typography.pxToRem(24),
        lineHeight: typography.pxToRem(28),
        padding: "18px"
      }
    },

    colorInherit: {
      "&": { ...colorStyles(blueGrey, variant) }
    },
    colorPrimary: {
      "&": { ...colorStyles(lightBlue, variant) }
    },
    colorSecondary: {
      "&": { ...colorStyles(lightBlue, variant) }
    }
  })
)
